import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles, AlertTriangle } from "lucide-react";
import { useTosVersion } from "@/components/TermsBody";
import { useReleaseMetadata } from "@/hooks/useReleaseMetadata";
import { ClosedLoopNotice } from "@/components/ClosedLoopNotice";

/** Compute age in years from an ISO YYYY-MM-DD date string. Returns null
 *  if the string is not a valid past date. Used for the Item 5 hard 21+
 *  age gate on the register form (matches the server's math). */
function computeAge(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  const dob = new Date(y, m - 1, d);
  if (Number.isNaN(dob.getTime())) return null;
  const today = new Date();
  if (dob > today) return null;
  let age = today.getFullYear() - dob.getFullYear();
  const monthDelta = today.getMonth() - dob.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < dob.getDate())) age -= 1;
  return age;
}

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const tosVersion = useTosVersion();
  const { data: release, isError: versionError, refetch: refreshVersion } = useReleaseMetadata();
  const [form, setForm] = useState({ email: "", password: "", display_name: "", date_of_birth: "" });
  const [agreedVersion, setAgreedVersion] = useState(null);
  const tosAgreed = !!release && agreedVersion === tosVersion;
  const [submitting, setSubmitting] = useState(false);
  const [dobError, setDobError] = useState("");
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const age = useMemo(() => computeAge(form.date_of_birth), [form.date_of_birth]);
  const ageValid = age !== null && age >= 21;

  // Native <input type="date"> max attribute: today's date so no future DOB
  // can be picked. Format required is YYYY-MM-DD.
  const todayIso = useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!tosAgreed || !release || versionError) return;
    setDobError("");
    if (!form.date_of_birth) {
      setDobError("Please enter your date of birth.");
      return;
    }
    if (age === null) {
      setDobError("That doesn't look like a valid date of birth.");
      return;
    }
    if (age < 21) {
      setDobError("You must be at least 21 years old to create a Playden account.");
      return;
    }
    setSubmitting(true);
    try {
      await register(form.email, form.password, form.display_name || undefined, form.date_of_birth, tosVersion);
      navigate("/collection");
    } catch (err) {
      if (err?.response?.status === 409) { setAgreedVersion(null); await refreshVersion(); }
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-16">
      <div className="hud-pill rounded-2xl p-8">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="h-5 w-5 text-emerald-300" />
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300">Register</div>
        </div>
        <h1 className="font-display text-3xl text-white mt-2 tracking-[0.06em]">JOIN VAULT</h1>
        <p className="text-white/50 text-sm mt-1">Start your collection. You&apos;ll receive 500 credits to begin.</p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Display Name</Label>
            <Input data-testid="register-name-input" value={form.display_name} onChange={(e) => update("display_name", e.target.value)} className="mt-1.5 bg-[hsl(220_18%_12%)] border-white/10" placeholder="collector_x" />
          </div>
          <div>
            <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Email</Label>
            <Input data-testid="register-email-input" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required className="mt-1.5 bg-[hsl(220_18%_12%)] border-white/10" />
          </div>
          <div>
            <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Password</Label>
            <Input data-testid="register-password-input" type="password" minLength={6} value={form.password} onChange={(e) => update("password", e.target.value)} required className="mt-1.5 bg-[hsl(220_18%_12%)] border-white/10" />
          </div>
          <div>
            <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Date of Birth</Label>
            <Input
              data-testid="register-dob-input"
              type="date"
              max={todayIso}
              value={form.date_of_birth}
              onChange={(e) => { update("date_of_birth", e.target.value); setDobError(""); }}
              required
              className="mt-1.5 bg-[hsl(220_18%_12%)] border-white/10 text-white"
            />
            <p className="text-[11px] text-white/40 mt-1.5">
              Playden is 21+ only. Your date of birth is stored privately and used to verify your age.
            </p>
            {form.date_of_birth && age !== null && (
              <p
                data-testid="register-dob-age-hint"
                className={`text-[11px] mt-1 font-mono ${ageValid ? "text-emerald-300/80" : "text-rose-300"}`}
              >
                Age: {age} · {ageValid ? "eligible" : "must be 21+"}
              </p>
            )}
            {dobError && (
              <div data-testid="register-dob-error" className="mt-2 flex items-start gap-1.5 text-[11px] text-rose-300">
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                <span>{dobError}</span>
              </div>
            )}
          </div>
          <ClosedLoopNotice variant="card" withBusinessModel testid="register-closed-loop" />
          <div className="flex items-start gap-2.5 rounded-lg border border-white/10 bg-[hsl(220_18%_12%)] p-3">
            <Checkbox
              id="tos-agree"
              data-testid="register-tos-checkbox"
              checked={tosAgreed}
              onCheckedChange={(v) => setAgreedVersion(v === true ? tosVersion : null)}
              disabled={!release || versionError}
              className="mt-0.5"
            />
            <label htmlFor="tos-agree" className="text-xs text-white/60 leading-relaxed cursor-pointer">
              I confirm the date of birth above is mine, I am 21+, and I have read and agree to the{" "}
              <Link to="/terms" target="_blank" data-testid="register-tos-link" className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2">
                Terms of Service (V {tosVersion})
              </Link>
              , including the Mint Recall &amp; credit-compensation policy, binding arbitration, and class-action waiver.
            </label>
          </div>
          <Button
            type="submit"
            data-testid="register-submit-button"
            disabled={submitting || !tosAgreed || !ageValid || !release || versionError}
            className="w-full bg-emerald-400 text-black hover:bg-emerald-300 disabled:opacity-50"
          >
            {submitting ? "Creating account…" : "Create Account"}
          </Button>
        </form>
        <div className="text-center text-sm text-white/50 mt-6">
          Already have an account? <Link to="/login" className="text-cyan-300 hover:text-cyan-200">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
